import React, { useState, useRef, useEffect, useCallback } from 'react';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
  faPaperPlane, faRobot, faUser, faSpinner, faTrash, faPlus,
  faPenToSquare, faMessage, faCopy, faRotate, faCheck,
  faVolumeHigh, faVolumeXmark
} from '@fortawesome/free-solid-svg-icons';
import { copyToClipboard } from '../utils/clipboard';

export default function Chatbot() {
  const API_BASE_URL = window.API_BASE_URL;
  const [conversations, setConversations] = useState([]);
  const [activeId, setActiveId] = useState(null);          // null = new (unsent) chat
  const [messages, setMessages] = useState([]);
  const [input, setInput] = useState('');
  const [sending, setSending] = useState(false);
  const [loadingList, setLoadingList] = useState(true);
  const [loadingConv, setLoadingConv] = useState(false);
  const [error, setError] = useState('');
  const [renamingId, setRenamingId] = useState(null);
  const [renameValue, setRenameValue] = useState('');
  const [copiedIdx, setCopiedIdx] = useState(null);
  const [playingIdx, setPlayingIdx] = useState(null);

  // Stop any in-flight speech when the user leaves the page or switches chats.
  useEffect(() => {
    return () => {
      if (typeof window !== 'undefined' && 'speechSynthesis' in window) {
        window.speechSynthesis.cancel();
      }
    };
  }, []);
  // Also cancel speech when the user opens a different conversation.
  useEffect(() => {
    if (typeof window !== 'undefined' && 'speechSynthesis' in window) {
      window.speechSynthesis.cancel();
      setPlayingIdx(null);
    }
  }, [activeId]);
  const listRef = useRef(null);
  const textareaRef = useRef(null);

  const authHeaders = useCallback((extra = {}) => {
    const token = localStorage.getItem('authToken');
    return {
      ...(token ? { 'Authorization': `Bearer ${token}` } : {}),
      ...extra
    };
  }, []);

  // Load the user's conversation list once
  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const res = await fetch(`${API_BASE_URL}/chatbot/conversations`, { headers: authHeaders() });
        const data = await res.json();
        if (!cancelled && data.success) {
          setConversations(data.conversations || []);
        }
      } catch (err) {
        if (!cancelled) setError('Failed to load conversations: ' + err.message);
      } finally {
        if (!cancelled) setLoadingList(false);
      }
    })();
    return () => { cancelled = true; };
  }, [API_BASE_URL, authHeaders]);

  // Auto-scroll to bottom on new messages
  useEffect(() => {
    if (listRef.current) listRef.current.scrollTop = listRef.current.scrollHeight;
  }, [messages, sending]);

  const openConversation = async (id) => {
    if (sending || id === activeId) return;
    setActiveId(id);
    setMessages([]);
    setError('');
    setLoadingConv(true);
    try {
      const res = await fetch(`${API_BASE_URL}/chatbot/conversations/${id}`, { headers: authHeaders() });
      const data = await res.json();
      if (!res.ok || !data.success) throw new Error(data.message || `HTTP ${res.status}`);
      setMessages((data.messages || []).map(m => ({ role: m.role, content: m.content })));
    } catch (err) {
      setError('Failed to load chat: ' + err.message);
    } finally {
      setLoadingConv(false);
    }
  };

  const newChat = () => {
    if (sending) return;
    setActiveId(null);
    setMessages([]);
    setInput('');
    setError('');
    if (textareaRef.current) textareaRef.current.focus();
  };

  const sendMessage = async () => {
    const text = input.trim();
    if (!text || sending) return;

    setMessages(prev => [...prev, { role: 'user', content: text }]);
    setInput('');
    setError('');
    setSending(true);

    try {
      const res = await fetch(`${API_BASE_URL}/chatbot/message`, {
        method: 'POST',
        headers: authHeaders({ 'Content-Type': 'application/json' }),
        body: JSON.stringify({
          message: text,
          conversation_id: activeId || undefined
        })
      });
      const data = await res.json();
      if (!res.ok || !data.success) throw new Error(data.message || `HTTP ${res.status}`);

      const reply = (data.reply || '').trim() || '(no response)';
      setMessages(prev => [...prev, { role: 'assistant', content: reply }]);

      // First send creates a conversation server-side; adopt that id locally
      if (!activeId && data.conversation_id) {
        setActiveId(data.conversation_id);
      }

      // Refresh sidebar list so the new/touched conversation jumps to the top
      const listRes = await fetch(`${API_BASE_URL}/chatbot/conversations`, { headers: authHeaders() });
      const listData = await listRes.json();
      if (listData.success) setConversations(listData.conversations || []);
    } catch (err) {
      setError(err.message || 'Failed to reach the chatbot');
      // Roll back the user message so they can retry
      setMessages(prev => prev.slice(0, -1));
      setInput(text);
    } finally {
      setSending(false);
      if (textareaRef.current) textareaRef.current.focus();
    }
  };

  const onKeyDown = (e) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      sendMessage();
    }
  };

  const deleteConversation = async (id) => {
    if (!window.confirm('Delete this conversation? This cannot be undone.')) return;
    try {
      const res = await fetch(`${API_BASE_URL}/chatbot/conversations/${id}`, {
        method: 'DELETE',
        headers: authHeaders()
      });
      const data = await res.json();
      if (!res.ok || !data.success) throw new Error(data.message || `HTTP ${res.status}`);
      setConversations(prev => prev.filter(c => c.id !== id));
      if (activeId === id) {
        setActiveId(null);
        setMessages([]);
      }
    } catch (err) {
      setError('Delete failed: ' + err.message);
    }
  };

  const beginRename = (conv) => {
    setRenamingId(conv.id);
    setRenameValue(conv.title || '');
  };

  const commitRename = async () => {
    const id = renamingId;
    const title = renameValue.trim();
    setRenamingId(null);
    if (!id || !title) return;
    const original = conversations.find(c => c.id === id);
    if (!original || original.title === title) return;
    setConversations(prev => prev.map(c => c.id === id ? { ...c, title } : c));
    try {
      const res = await fetch(`${API_BASE_URL}/chatbot/conversations/${id}`, {
        method: 'PUT',
        headers: authHeaders({ 'Content-Type': 'application/json' }),
        body: JSON.stringify({ title })
      });
      const data = await res.json();
      if (!res.ok || !data.success) throw new Error(data.message || `HTTP ${res.status}`);
    } catch (err) {
      // revert on failure
      setConversations(prev => prev.map(c => c.id === id ? { ...c, title: original.title } : c));
      setError('Rename failed: ' + err.message);
    }
  };

  const onRenameKey = (e) => {
    if (e.key === 'Enter') { e.preventDefault(); commitRename(); }
    else if (e.key === 'Escape') { setRenamingId(null); }
  };

  const handleListen = (idx, content) => {
    if (typeof window === 'undefined' || !('speechSynthesis' in window)) {
      setError('Text-to-speech is not supported in this browser.');
      return;
    }
    const synth = window.speechSynthesis;

    // Toggle: clicking the same row again stops playback.
    if (playingIdx === idx) {
      synth.cancel();
      setPlayingIdx(null);
      return;
    }

    // Stop whatever is currently speaking, then start the new utterance.
    synth.cancel();
    const text = String(content || '').trim();
    if (!text) return;

    const utter = new SpeechSynthesisUtterance(text);
    utter.rate = 1;
    utter.pitch = 1;
    // Lightweight script detection so Urdu / Arabic replies pick the right voice
    // when one is installed on the user's system.
    if (/[؀-ۿ]/.test(text)) {
      utter.lang = 'ur-PK';
    }
    utter.onend = () => setPlayingIdx(prev => (prev === idx ? null : prev));
    utter.onerror = () => setPlayingIdx(prev => (prev === idx ? null : prev));

    setPlayingIdx(idx);
    synth.speak(utter);
  };

  const handleCopy = async (idx, content) => {
    const ok = await copyToClipboard(content || '');
    if (ok) {
      setCopiedIdx(idx);
      setTimeout(() => setCopiedIdx(c => (c === idx ? null : c)), 1500);
    } else {
      setError('Failed to copy to clipboard');
    }
  };

  const handleRegenerate = async () => {
    if (!activeId || sending) return;
    // Find the index of the last assistant message — that's the one we replace.
    let lastIdx = -1;
    for (let i = messages.length - 1; i >= 0; i--) {
      if (messages[i].role === 'assistant') { lastIdx = i; break; }
    }
    if (lastIdx < 0) return;
    const original = messages[lastIdx];

    setError('');
    setSending(true);
    // Show the placeholder bubble while waiting
    setMessages(prev => prev.map((m, i) => i === lastIdx ? { ...m, content: '', _regenerating: true } : m));

    try {
      const res = await fetch(`${API_BASE_URL}/chatbot/regenerate`, {
        method: 'POST',
        headers: authHeaders({ 'Content-Type': 'application/json' }),
        body: JSON.stringify({ conversation_id: activeId })
      });
      const data = await res.json();
      if (!res.ok || !data.success) throw new Error(data.message || `HTTP ${res.status}`);
      const newReply = (data.reply || '').trim() || '(no response)';
      setMessages(prev => prev.map((m, i) => i === lastIdx ? { role: 'assistant', content: newReply } : m));

      // Bump sidebar (regenerate also touches the conversation server-side).
      const listRes = await fetch(`${API_BASE_URL}/chatbot/conversations`, { headers: authHeaders() });
      const listData = await listRes.json();
      if (listData.success) setConversations(listData.conversations || []);
    } catch (err) {
      setError(err.message || 'Regenerate failed');
      // Restore the previous assistant content
      setMessages(prev => prev.map((m, i) => i === lastIdx ? original : m));
    } finally {
      setSending(false);
    }
  };

  return (
    <div style={{
      display: 'flex',
      // main already has top padding equal to (header height + 12px) and 12px
      // on the sides/bottom. Subtract that from 100vh and account for our own
      // breathing room so the layout never forces the page to scroll.
      height: 'calc(100vh - var(--header-height, 52px) - 24px)',
      width: '100%',
      maxWidth: 'none',
      margin: 0,
      gap: 12,
      padding: 0,
      boxSizing: 'border-box'
    }}>
      {/* SIDEBAR */}
      <aside style={{
        width: 260,
        flexShrink: 0,
        background: 'var(--panel-bg, white)',
        border: '1px solid var(--border-color, #e2e8f0)',
        borderRadius: 12,
        display: 'flex',
        flexDirection: 'column',
        overflow: 'hidden',
        minHeight: 0
      }}>
        <div style={{ padding: 12, borderBottom: '1px solid var(--border-color, #e2e8f0)' }}>
          <button
            onClick={newChat}
            disabled={sending}
            style={{
              width: '100%',
              padding: '10px 12px',
              background: sending ? '#cbd5e1' : 'linear-gradient(135deg, #7c3aed, #6d28d9)',
              color: 'white',
              border: 'none',
              borderRadius: 10,
              fontSize: 14,
              fontWeight: 700,
              cursor: sending ? 'not-allowed' : 'pointer',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              gap: 8
            }}
          >
            <FontAwesomeIcon icon={faPlus} /> New chat
          </button>
        </div>

        <div style={{ flex: 1, overflowY: 'auto', padding: 8 }}>
          <div style={{ fontSize: 11, fontWeight: 700, color: 'var(--text-muted, #94a3b8)', padding: '6px 8px', textTransform: 'uppercase', letterSpacing: 0.5 }}>
            Recents
          </div>
          {loadingList ? (
            <div style={{ padding: 12, color: 'var(--text-muted)', fontSize: 13 }}>
              <FontAwesomeIcon icon={faSpinner} spin /> Loading...
            </div>
          ) : conversations.length === 0 ? (
            <div style={{ padding: 12, color: 'var(--text-muted, #94a3b8)', fontSize: 13 }}>
              No chats yet. Start a new one!
            </div>
          ) : conversations.map((c) => {
            const isActive = c.id === activeId;
            const isRenaming = renamingId === c.id;
            return (
              <div
                key={c.id}
                onClick={() => !isRenaming && openConversation(c.id)}
                style={{
                  padding: '8px 10px',
                  borderRadius: 8,
                  marginBottom: 2,
                  cursor: isRenaming ? 'default' : 'pointer',
                  background: isActive ? 'var(--surface-hover, #ede9fe)' : 'transparent',
                  border: isActive ? '1px solid #c4b5fd' : '1px solid transparent',
                  display: 'flex',
                  alignItems: 'center',
                  gap: 8,
                  position: 'relative'
                }}
                onMouseEnter={(e) => { if (!isActive && !isRenaming) e.currentTarget.style.background = 'var(--surface, #f1f5f9)'; }}
                onMouseLeave={(e) => { if (!isActive && !isRenaming) e.currentTarget.style.background = 'transparent'; }}
              >
                <FontAwesomeIcon icon={faMessage} style={{ fontSize: 11, color: isActive ? '#7c3aed' : '#94a3b8', flexShrink: 0 }} />
                {isRenaming ? (
                  <input
                    autoFocus
                    value={renameValue}
                    onChange={(e) => setRenameValue(e.target.value)}
                    onBlur={commitRename}
                    onKeyDown={onRenameKey}
                    style={{
                      flex: 1,
                      border: '1px solid #c4b5fd',
                      borderRadius: 6,
                      padding: '4px 6px',
                      fontSize: 13,
                      outline: 'none'
                    }}
                  />
                ) : (
                  <>
                    <span style={{
                      flex: 1,
                      fontSize: 13,
                      color: 'var(--text-primary, #0f172a)',
                      overflow: 'hidden',
                      textOverflow: 'ellipsis',
                      whiteSpace: 'nowrap'
                    }}>
                      {c.title || 'Untitled'}
                    </span>
                    <button
                      onClick={(e) => { e.stopPropagation(); beginRename(c); }}
                      title="Rename"
                      style={{ background: 'transparent', border: 'none', cursor: 'pointer', color: '#94a3b8', padding: 2, fontSize: 11 }}
                    >
                      <FontAwesomeIcon icon={faPenToSquare} />
                    </button>
                    <button
                      onClick={(e) => { e.stopPropagation(); deleteConversation(c.id); }}
                      title="Delete"
                      style={{ background: 'transparent', border: 'none', cursor: 'pointer', color: '#dc2626', padding: 2, fontSize: 11 }}
                    >
                      <FontAwesomeIcon icon={faTrash} />
                    </button>
                  </>
                )}
              </div>
            );
          })}
        </div>
      </aside>

      {/* MAIN CHAT */}
      <main style={{
        flex: 1,
        display: 'flex',
        flexDirection: 'column',
        background: 'var(--panel-bg, white)',
        border: '1px solid var(--border-color, #e2e8f0)',
        borderRadius: 12,
        overflow: 'hidden',
        minHeight: 0,
        minWidth: 0
      }}>
        <header style={{
          padding: '14px 18px',
          borderBottom: '1px solid var(--border-color, #e2e8f0)',
          display: 'flex',
          alignItems: 'center',
          gap: 10
        }}>
          <FontAwesomeIcon icon={faRobot} style={{ color: '#7c3aed', fontSize: 18 }} />
          <h2 style={{ margin: 0, fontSize: 18 }}>ChatFast</h2>
          <span style={{ marginLeft: 'auto', fontSize: 12, color: 'var(--text-muted, #94a3b8)' }}>
            {activeId ? (conversations.find(c => c.id === activeId)?.title || '') : 'New chat'}
          </span>
        </header>

        <div ref={listRef} style={{ flex: 1, overflowY: 'auto', padding: 18 }}>
          {loadingConv ? (
            <div style={{
              display: 'flex', alignItems: 'center', justifyContent: 'center',
              height: '100%', color: 'var(--text-muted, #94a3b8)', fontSize: 14
            }}>
              <FontAwesomeIcon icon={faSpinner} spin />&nbsp; Loading conversation...
            </div>
          ) : messages.length === 0 && !sending ? (
            <div style={{
              display: 'flex', alignItems: 'center', justifyContent: 'center',
              height: '100%', color: 'var(--text-muted, #94a3b8)', fontSize: 14, textAlign: 'center'
            }}>
              <div>
                <FontAwesomeIcon icon={faRobot} style={{ fontSize: 36, opacity: 0.4, marginBottom: 12 }} />
                <div>Ask anything to get started.</div>
                <div style={{ fontSize: 12, marginTop: 6 }}>Local AI · Enter to send · Shift+Enter for a new line</div>
              </div>
            </div>
          ) : (
            <>
              {(() => {
                let lastAssistantIdx = -1;
                for (let i = messages.length - 1; i >= 0; i--) {
                  if (messages[i].role === 'assistant' && !messages[i]._regenerating) { lastAssistantIdx = i; break; }
                }
                return messages.map((m, i) => (
                  <Bubble
                    key={i}
                    role={m.role}
                    content={m.content}
                    thinking={!!m._regenerating}
                    canCopy={m.role === 'assistant' && !m._regenerating && !!m.content}
                    canRegenerate={i === lastAssistantIdx && !!activeId && !sending}
                    canListen={m.role === 'assistant' && !m._regenerating && !!m.content}
                    copied={copiedIdx === i}
                    isPlaying={playingIdx === i}
                    onCopy={() => handleCopy(i, m.content)}
                    onRegenerate={handleRegenerate}
                    onListen={() => handleListen(i, m.content)}
                  />
                ));
              })()}
              {sending && messages[messages.length - 1]?.role === 'user' && (
                <Bubble role="assistant" content="" thinking />
              )}
            </>
          )}
        </div>

        {error && (
          <div style={{
            background: '#fee2e2', color: '#b91c1c',
            border: '1px solid #fecaca',
            padding: '8px 12px', borderRadius: 8,
            margin: '0 18px 10px', fontSize: 13
          }}>
            {error}
          </div>
        )}

        <div style={{
          display: 'flex', gap: 8, alignItems: 'flex-end',
          padding: 12, borderTop: '1px solid var(--border-color, #e2e8f0)'
        }}>
          <textarea
            ref={textareaRef}
            value={input}
            onChange={(e) => setInput(e.target.value)}
            onKeyDown={onKeyDown}
            placeholder="Type your message…"
            rows={2}
            disabled={sending || loadingConv}
            style={{
              flex: 1,
              padding: '10px 12px',
              border: '1px solid var(--border-color, #cbd5e1)',
              borderRadius: 10,
              fontSize: 14,
              fontFamily: 'inherit',
              resize: 'vertical',
              minHeight: 44,
              maxHeight: 200,
              outline: 'none',
              background: 'var(--panel-bg, white)',
              color: 'var(--text-primary, #0f172a)'
            }}
          />
          <button
            onClick={sendMessage}
            disabled={sending || !input.trim()}
            style={{
              padding: '10px 18px',
              background: (sending || !input.trim()) ? '#94a3b8' : 'linear-gradient(135deg, #7c3aed, #6d28d9)',
              color: 'white',
              border: 'none',
              borderRadius: 10,
              fontSize: 14,
              fontWeight: 700,
              cursor: (sending || !input.trim()) ? 'not-allowed' : 'pointer',
              boxShadow: (sending || !input.trim()) ? 'none' : '0 2px 8px rgba(124,58,237,0.35)',
              display: 'inline-flex',
              alignItems: 'center',
              gap: 8,
              minWidth: 100,
              justifyContent: 'center'
            }}
          >
            <FontAwesomeIcon icon={sending ? faSpinner : faPaperPlane} spin={sending} />
            {sending ? 'Sending' : 'Send'}
          </button>
        </div>
      </main>
    </div>
  );
}

function Bubble({
  role, content, thinking,
  canCopy, canRegenerate, canListen,
  copied, isPlaying,
  onCopy, onRegenerate, onListen
}) {
  const isUser = role === 'user';
  const showActions = !isUser && (canCopy || canRegenerate || canListen);
  return (
    <div style={{
      display: 'flex',
      gap: 10,
      marginBottom: 14,
      flexDirection: isUser ? 'row-reverse' : 'row',
      alignItems: 'flex-start'
    }}>
      <div style={{
        width: 32, height: 32, borderRadius: '50%',
        background: isUser ? '#3b82f6' : '#7c3aed',
        color: 'white',
        display: 'flex', alignItems: 'center', justifyContent: 'center',
        fontSize: 14, flexShrink: 0
      }}>
        <FontAwesomeIcon icon={isUser ? faUser : faRobot} />
      </div>
      <div style={{ maxWidth: '78%', display: 'flex', flexDirection: 'column', alignItems: isUser ? 'flex-end' : 'flex-start' }}>
        <div style={{
          padding: '10px 14px', borderRadius: 12,
          background: isUser ? '#dbeafe' : 'var(--surface, #f1f5f9)',
          color: 'var(--text-primary, #0f172a)',
          fontSize: 14, lineHeight: 1.55,
          whiteSpace: 'pre-wrap', wordBreak: 'break-word'
        }}>
          {thinking
            ? <span style={{ color: 'var(--text-muted, #64748b)', fontStyle: 'italic' }}>thinking…</span>
            : content
          }
        </div>
        {showActions && (
          <div style={{ display: 'flex', gap: 4, marginTop: 4 }}>
            {canCopy && (
              <button
                onClick={onCopy}
                title="Copy"
                style={actionBtnStyle}
                onMouseEnter={(e) => e.currentTarget.style.background = 'var(--surface-hover, #e2e8f0)'}
                onMouseLeave={(e) => e.currentTarget.style.background = 'transparent'}
              >
                <FontAwesomeIcon icon={copied ? faCheck : faCopy} style={{ fontSize: 11 }} />
                <span>{copied ? 'Copied' : 'Copy'}</span>
              </button>
            )}
            {canListen && (
              <button
                onClick={onListen}
                title={isPlaying ? 'Stop' : 'Listen'}
                style={{
                  ...actionBtnStyle,
                  background: isPlaying ? 'var(--surface-hover, #e2e8f0)' : 'transparent',
                  borderColor: isPlaying ? '#7c3aed' : 'var(--border-color, #e2e8f0)',
                  color: isPlaying ? '#7c3aed' : 'var(--text-secondary, #475569)'
                }}
                onMouseEnter={(e) => e.currentTarget.style.background = 'var(--surface-hover, #e2e8f0)'}
                onMouseLeave={(e) => { if (!isPlaying) e.currentTarget.style.background = 'transparent'; }}
              >
                <FontAwesomeIcon icon={isPlaying ? faVolumeXmark : faVolumeHigh} style={{ fontSize: 11 }} />
                <span>{isPlaying ? 'Stop' : 'Listen'}</span>
              </button>
            )}
            {canRegenerate && (
              <button
                onClick={onRegenerate}
                title="Regenerate response"
                style={actionBtnStyle}
                onMouseEnter={(e) => e.currentTarget.style.background = 'var(--surface-hover, #e2e8f0)'}
                onMouseLeave={(e) => e.currentTarget.style.background = 'transparent'}
              >
                <FontAwesomeIcon icon={faRotate} style={{ fontSize: 11 }} />
                <span>Regenerate</span>
              </button>
            )}
          </div>
        )}
      </div>
    </div>
  );
}

const actionBtnStyle = {
  display: 'inline-flex',
  alignItems: 'center',
  gap: 6,
  padding: '4px 10px',
  background: 'transparent',
  border: '1px solid var(--border-color, #e2e8f0)',
  borderRadius: 6,
  cursor: 'pointer',
  fontSize: 12,
  fontWeight: 500,
  color: 'var(--text-secondary, #475569)',
  transition: 'background 0.15s'
};
